import React, { useContext, useState } from "react";
import ComposeArea from "@src/components/ideas/ComposeArea";
import HLSVideo from "@src/components/ideas/HLSVideo";
import { navigate } from "gatsby";
import window from "global/window";
import ShareToClassStory from "@src/components/ideas/ShareToClassStory";
import { AppDataContext } from "@src/components/AppDataContext";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import {
  BruceDiscuss,
  BruceDiscussBalloon,
  discussion,
  isFirst,
  IsHtml,
  isNext,
  isPrev,
  IsShare,
  IsVideo,
  isVideo16per9,
  KatieDiscuss,
  KatieDiscussBalloon,
  LessonSS,
  LessonSSBody,
  LessonSSClose,
  LessonSSContainer,
  LessonSSCrumbs,
  LessonSSHeader,
  LessonSSHeaderContainer,
  LessonSSHtmlSlideContent,
  LessonSSNav,
  LessonSSShareSlideContent,
  LessonSSSlideCompose,
  LessonSSSlideTitle,
  LessonSSTitle,
  MaterialIconArrowBack,
  MaterialIconArrowForward,
  MaterialIconCheck,
  MaterialIconClose,
  video,
} from "@src/components/ideas/LessonSlideShowStyles";
import { css } from "@emotion/core";
import Button from "@src/components/Button";
import _get from "lodash/get";

const OUTER_PROP = "slides";
const INNER_PROP = "slides";

const INITIAL_STATE = {
  outerIndex: 0,
  innerIndex: 0,
  firstView: true,
};

const IconArrowLeft = () => <MaterialIconArrowBack />;
const IconArrowRight = () => <MaterialIconArrowForward />;
const IconCheck = () => <MaterialIconCheck />;
const IconClose = () => <MaterialIconClose />;
const IconDiscussKatie = () => <KatieDiscuss />;
const IconDiscussKatieBalloon = () => <KatieDiscussBalloon />;
const IconDiscussBruce = () => <BruceDiscuss />;
const IconDiscussBruceBalloon = () => <BruceDiscussBalloon />;

function Slide(props) {
  if (props.slide.slide_type === "html") return <HtmlSlide slide={props.slide} discuss={props.discuss} />;
  if (props.slide.slide_type === "share") return <ShareSlide {...props} />;

  return <VideoSlide slide={props.slide} firstView={props.firstView} slug={props.lesson.slug} index={props.index} />;
}

function HtmlSlide({ slide, discuss }) {
  let content = slide.html_content;
  if (content) {
    content = content.replace(/\\n/g, "");
    content = content.replace(/\\t/g, "");
  }
  return (
    <IsHtml>
      <LessonSSHtmlSlideContent
        data-test-name="lessonSlideContent"
        css={discuss ? discussion : css``}
        dangerouslySetInnerHTML={{ __html: content }}
      />
      {discuss ? <IconDiscussKatie /> : null}
      {discuss ? <IconDiscussKatieBalloon /> : null}
      {discuss ? <IconDiscussBruce /> : null}
      {discuss ? <IconDiscussBruceBalloon /> : null}
    </IsHtml>
  );
}

function ShareSlide(props) {
  const appData = useContext(AppDataContext);
  const [shareText, setShareText] = useState(props.lesson.share_body);
  const canWatch = !!props.user; /*|| !props.lesson.require_auth*/

  const image = props.lesson.share_hero.localFile;
  const imagePath = image ? `https://static.classdojo.com/uploads/${image.name}.${image.extension}` : null;
  const attachment = { type: "photo", path: imagePath };
  let shareButton = null;
  if (_get(appData, "data.userData.type") === "teacher") {
    shareButton =
      canWatch && props.share ? (
        <ShareToClassStory {...props.share} image={imagePath} body={shareText} classes={appData.data.classes} />
      ) : (
        <ShareBlocker />
      );
  }

  return (
    <IsShare>
      <LessonSSSlideTitle>{props.slide.subtitle}</LessonSSSlideTitle>
      <LessonSSShareSlideContent>
        <LessonSSSlideCompose>
          <ComposeArea
            avatarURL={props.user ? props.user.avatarURL : ""}
            initialText={shareText}
            attachment={attachment}
            submitComponent={shareButton}
            onTextChange={setShareText}
          />
        </LessonSSSlideCompose>
      </LessonSSShareSlideContent>
    </IsShare>
  );
}

const ShareBlocker = () => {
  const modals = useContext(ModalContext);
  return (
    <div>
      <Button onClick={() => modals.showModal(ModalType.TeacherLogin, { form: { noRedirect: true } })}>
        Share on Class Story
      </Button>
    </div>
  );
};

function VideoSlide({ slide, firstView, slug, index }) {
  let player = null;
  if (slide.slide_type === "hls") {
    player = <VideoPlayerHLS {...slide} firstView={firstView} slug={slug} index={index} />;
  } else if (slide.slide_type === "vidly") {
    player = <VideoPlayer {...slide} firstView={firstView} />;
  } else {
    player = <AltPlayer {...slide} firstView={firstView} />;
  }

  return <IsVideo id={`${index}-${slug}`}>{player}</IsVideo>;
}

const jwSrc = (url, firstView) => {
  return `https://content.jwplatform.com/players/${url}.html?hlshtml=true&autoplay=${firstView}`;
};

function VideoPlayerHLS(props) {
  return (
    <div data-test-name="videoPlayerHLS" className="is-video-inner">
      <div css={css(video, isVideo16per9)}>
        <HLSVideo
          url={props.hls_url}
          poster={props.hls_poster_url}
          captions={props.hls_captions}
          autoplay={false}
          slug={props.slug}
          index={props.index}
        />
      </div>
    </div>
  );
}

function VideoPlayer(props) {
  return (
    <div className="is-video-inner">
      <div css={css(video, isVideo16per9)}>
        <iframe frameBorder="0" allowFullScreen name="video-frame" src={jwSrc(props.video_id, props.firstView)} />
      </div>
    </div>
  );
}

const youTubeSrc = (url, firstView) => {
  return `https://www.youtube.com/embed/${url}?rel=0&showinfo=0&autoplay=${firstView ? 1 : 0}`;
};

function AltPlayer(props) {
  return (
    <div className="is-video-inner">
      <div css={css(video, isVideo16per9)}>
        <iframe frameBorder="0" allowFullScreen name="video-frame" src={youTubeSrc(props.video_id, props.firstView)} />
      </div>
    </div>
  );
}

// small helper functions
function outerPropMaxIndex(data) {
  return data[OUTER_PROP].length - 1;
}

function innerPropMaxIndex(data, outerIndex) {
  return data[OUTER_PROP][outerIndex][INNER_PROP].length - 1;
}

// just a sanity check while developing, to make sure we didn't screw something up
function checkValid(state, data) {
  if (data[OUTER_PROP][state.outerIndex][INNER_PROP][state.innerIndex] == null) {
    throw new Error("Invalid indexes into data!");
  }
}

// adds boolean flags that tell if we're at the start or end
// helps keep the code in the component from knowing too much about how this
// all works
function addBoundaryFlags(state, data) {
  // sanity check
  checkValid(state, data);

  // check if it's only one section and one slide
  if (outerPropMaxIndex(data) === 0 && innerPropMaxIndex(data, 0) === 0) {
    return { ...state, atStart: true, atEnd: true };
  }
  // check if we're at very start, and add special `atStart` flag
  if (state.outerIndex === 0 && state.innerIndex === 0) {
    return { ...state, atStart: true, atEnd: false };
  }
  // check if we're at very end, and add special `atEnd` flag
  if (outerPropMaxIndex(data) === state.outerIndex && innerPropMaxIndex(data, state.outerIndex) === state.innerIndex) {
    return { ...state, atStart: false, atEnd: true };
  }

  return { ...state, atStart: false, atEnd: false };
}

function forward(state, data, logEvent) {
  if (state.atEnd) {
    logEvent({ eventName: "ideas.activityEnd", eventValue: data.url_slug });
    return state;
  }

  // we have room to advance the inner index by 1 without moving the outer index
  if (state.innerIndex < innerPropMaxIndex(data, state.outerIndex)) {
    return addBoundaryFlags({ outerIndex: state.outerIndex, innerIndex: state.innerIndex + 1 }, data);
  }
  // we have to move the outer index forward 1, and set the inner index to 0
  const outerIndex = state.outerIndex + 1;
  const innerIndex = 0;
  logEvent({
    eventName: `ideas.slideshowViewSection${outerIndex}${data.slides[outerIndex].title}`,
    eventValue: data.url_slug,
  });
  // this shouldn't happen, because state.atEnd should catch it
  // but while developing this tricky thing, let's catch it early
  if (outerIndex > outerPropMaxIndex(data)) {
    throw new Error(
      `Outer index greater than max; old: ${JSON.stringify(state)}, new: ${JSON.stringify({
        outerIndex,
        innerIndex,
      })}`,
    );
  }

  return addBoundaryFlags({ outerIndex, innerIndex }, data);
}

function back(state, data, logEvent) {
  if (state.atStart) return state;
  // we have room to move the inner index back by 1 without moving the outer index
  if (state.innerIndex > 0) {
    return addBoundaryFlags({ outerIndex: state.outerIndex, innerIndex: state.innerIndex - 1 }, data);
  }
  // we need to move the outer index back 1, and set the inner index to max
  const outerIndex = state.outerIndex - 1;
  const innerIndex = innerPropMaxIndex(data, outerIndex);
  logEvent({
    eventName: `ideas.slideshowViewSection${outerIndex}${data.slides[outerIndex].title}`,
    eventValue: data.url_slug,
  });
  // this shouldn't happen, because state.atStart should catch it
  // but while developing this tricky thing, let's catch it early
  if (outerIndex < 0) {
    throw new Error(
      `Outer index less than 0; old: ${JSON.stringify(state)}, new: ${JSON.stringify({
        outerIndex,
        innerIndex,
      })}`,
    );
  }
  return addBoundaryFlags({ outerIndex, innerIndex }, data);
}

function goTo(newState, data) {
  const outerIndex = newState.outerIndex;
  const innerIndex = newState.innerIndex;
  return addBoundaryFlags({ outerIndex, innerIndex }, data);
}

class LessonSlideShow extends React.Component<any, any> {
  constructor(props) {
    super(props);

    const isShare = this.props.tab === "share";
    const isNumber = /^\d+$/.test(this.props.tab);

    if (isShare) {
      INITIAL_STATE.outerIndex = this.props.lesson.slides.length - 1;
      INITIAL_STATE.innerIndex = 0;
    } else if (
      isNumber &&
      parseInt(this.props.tab) >= 0 &&
      parseInt(this.props.tab) < this.props.lesson.slides.length
    ) {
      INITIAL_STATE.outerIndex = parseInt(this.props.tab);
      INITIAL_STATE.innerIndex = 0;
    } else {
      INITIAL_STATE.outerIndex = 0;
      INITIAL_STATE.innerIndex = 0;
    }

    this.state = Object.assign(addBoundaryFlags(INITIAL_STATE, this.props.lesson));
  }

  componentDidMount() {
    window.addEventListener("keyup", this._handleKeyUp);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!!this.props.lesson && !!this.props.lesson.slides && this.props.lesson !== nextProps.lesson) {
      this.setState(addBoundaryFlags(INITIAL_STATE, nextProps.lesson));
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this._handleKeyUp);
  }

  render() {
    const { lesson, user } = this.props;
    const { outerIndex, innerIndex } = this.state;
    const currentSection = lesson[OUTER_PROP][outerIndex];
    const currentSlide = currentSection[INNER_PROP][innerIndex];

    const share =
      this.props.user && this.props.user.role !== "parent" && this.props.user.role !== "student"
        ? {
            classes: this.props.classes,
            slug: this.props.lesson.slug,
            onShare: this.props.shareToClassStory,
            shareStatus: this.props.shareStatus,
            shareBodyChanged: (e) => this.setState({ shareBody: e.target.value }),
          }
        : false;

    const crumbs = lesson.slides.map((section, i) => {
      let className = "lesson-ss__crumbs__item";
      className += i === outerIndex ? " is-active" : "";
      const slideCrumbs = this._slideCrumbs(i === outerIndex, currentSection, innerIndex);
      const hasSlideCrumbs = !!slideCrumbs;
      className += hasSlideCrumbs ? " has-multiple-slides" : "";
      return (
        <li
          key={i}
          className={className}
          onClick={() => this.handleSectionClick(this._goTo, section.title, i, lesson.slug)}
        >
          <div>
            <div className="lesson-ss__crumbs__item__title">{section.title}</div>
            <div className="lesson-ss__crumbs__item__slides">{slideCrumbs}</div>
          </div>
        </li>
      );
    });

    const isSingleSlide = this.props.lesson.slides.length === 1;
    const isLastSlide =
      this.props.lesson.slides.length - 1 === outerIndex && currentSection.slides.length - 1 === innerIndex;
    const navPrevClassName = [isPrev];
    if (outerIndex === 0 && innerIndex === 0) {
      navPrevClassName.push(isFirst);
    }
    const navNextClassName = [isNext];
    const isDiscussion = currentSection.title === "Discuss";

    const headerStyle = isSingleSlide ? { height: 100 } : {};

    return (
      <LessonSS>
        <LessonSSContainer>
          <LessonSSClose to={`/i/${lesson.slug}`} data-test-name="lessonClose">
            <IconClose />
          </LessonSSClose>
          <LessonSSHeader style={headerStyle}>
            <LessonSSHeaderContainer>
              <LessonSSTitle>{lesson.title}</LessonSSTitle>
              {!isSingleSlide && (
                <LessonSSCrumbs>
                  <ul>{crumbs}</ul>
                </LessonSSCrumbs>
              )}
            </LessonSSHeaderContainer>
          </LessonSSHeader>
          <LessonSSBody>
            <Slide
              key={outerIndex * 100 + innerIndex}
              index={outerIndex * 100 + innerIndex}
              slide={currentSlide}
              firstView={this.state.firstView}
              lesson={lesson}
              user={user}
              share={share ? Object.assign({}, share, { shareBody: this.props.lesson.share_body }) : false}
              discuss={isDiscussion}
            />
            {!isSingleSlide && (
              <LessonSSNav>
                <div css={css(navPrevClassName)} onClick={() => this._back()}>
                  <IconArrowLeft />
                </div>
                <div css={css(navNextClassName)} onClick={() => this._forward()}>
                  {isLastSlide ? <IconCheck /> : <IconArrowRight />}
                </div>
              </LessonSSNav>
            )}
          </LessonSSBody>
        </LessonSSContainer>
      </LessonSS>
    );
  }

  handleSectionClick = (_, sectionName, sectionIndex, lessonSlug) => {
    this._goTo(sectionIndex, 0);
    this.props.logEvent({
      eventName: `ideas.slideshowViewSection${sectionIndex}${sectionName}`,
      eventValue: lessonSlug,
    });
  };

  _checkFirst = (newState) => {
    return this.state.firstView ? Object.assign({}, { firstView: false }, newState) : newState;
  };

  _forward = () => {
    this.state.atEnd
      ? this._goToLessonInfo()
      : this.setState(this._checkFirst(forward(this.state, this.props.lesson, this.props.logEvent)));
  };

  _back = () => {
    this.state.atStart
      ? this._goToLessonInfo()
      : this.setState(this._checkFirst(back(this.state, this.props.lesson, this.props.logEvent)));
  };

  _goTo = (outerIndex, innerIndex) => {
    this.setState(this._checkFirst(goTo({ outerIndex, innerIndex }, this.props.lesson)));
  };

  _handleKeyUp = (e) => {
    if (e.keyCode === 27) {
      this._goToLessonInfo();
    } else if (e.keyCode === 37) {
      this._back();
    } else if (e.keyCode === 39) {
      this._forward();
    }
  };

  _goToLessonInfo = () => {
    navigate(`/i/${this.props.lesson.slug}`);
  };

  _slideCrumbs = (active, currentSection, innerIndex) => {
    if (!active || (currentSection.slides && currentSection.slides.length < 2)) return null;
    const crumbs = currentSection.slides.map((_, i) => (
      <li key={i} className={i === innerIndex ? "is-active" : null} />
    ));
    return <ul className="lesson-ss__crumbs__item__list">{crumbs}</ul>;
  };
}

export default LessonSlideShow;
